import { apiClient } from './client';

/**
 * Client for the in-app notifications inbox (Foundations 2026-07-21).
 *
 * Mirrors server/src/routes/notifications.routes.ts:
 *   - GET  /api/notifications              — the caller's inbox (cursor)
 *   - GET  /api/notifications/unread-count — the bell badge number
 *   - POST /api/notifications/:id/read     — mark one read
 *   - POST /api/notifications/read-all     — mark every unread read
 *
 * Every endpoint is scoped server-side to the caller's own inbox.
 */

export interface Notification {
  id: string;
  organizationId: string;
  recipientMembershipId: string;
  /** Category slug driving the icon, e.g. 'task_assigned'. */
  kind: string;
  title: string;
  body: string | null;
  /** In-app relative path to navigate to on click. */
  linkPath: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResult {
  items: Notification[];
  hasMore: boolean;
}

export interface ListNotificationsQuery {
  unreadOnly?: boolean;
  /** Page size, clamped server-side to [1, 50]. */
  limit?: number;
  /** Cursor — an ISO createdAt; returns only rows strictly older. */
  before?: string;
}

export function serializeNotificationQuery(query: ListNotificationsQuery = {}): URLSearchParams {
  const params = new URLSearchParams();
  if (query.unreadOnly) params.set('unreadOnly', 'true');
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.before) params.set('before', query.before);
  return params;
}

export async function listNotifications(query: ListNotificationsQuery = {}): Promise<NotificationListResult> {
  const qs = serializeNotificationQuery(query).toString();
  const res = await apiClient.get<{ data: NotificationListResult }>(
    `/api/notifications${qs ? `?${qs}` : ''}`,
  );
  return res.data.data;
}

export async function getUnreadNotificationCount(): Promise<number> {
  const res = await apiClient.get<{ data: { count: number } }>('/api/notifications/unread-count');
  return res.data.data.count;
}

export async function markNotificationRead(id: string): Promise<Notification> {
  const res = await apiClient.post<{ data: Notification }>(
    `/api/notifications/${encodeURIComponent(id)}/read`,
    {},
  );
  return res.data.data;
}

export async function markAllNotificationsRead(): Promise<{ count: number }> {
  const res = await apiClient.post<{ data: { count: number } }>('/api/notifications/read-all', {});
  return res.data.data;
}
